import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class KeycloakInitService {
  private hasRegisteredExpiryGuards = false;
  private isLoggingOut = false;
  private readonly activityEvents = ['pointerdown', 'keydown', 'touchstart'];

  private readonly handleUserActivity = (event: Event): void => {
    if (!this.isSessionExpired()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    void this.forceLogout();
  };

  private readonly handleWindowFocus = (): void => {
    if (this.isSessionExpired()) {
      void this.forceLogout();
    }
  };

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible' && this.isSessionExpired()) {
      void this.forceLogout();
    }
  };

  constructor(
    private readonly keycloakService: KeycloakService
  ) {}

  async initKeycloak(): Promise<void> {
    try {
      await this.keycloakService.init({
        config: {
          url: environment.KEYCLOAK_URL,
          realm: environment.KEYCLOAK_REALM,
          clientId: environment.KEYCLOAK_CLIENT_ID,
        },
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri:
            globalThis.location.origin + '/assets/silent-check-sso.html',
          pkceMethod: 'S256',
        },
        enableBearerInterceptor: true,
        bearerPrefix: 'Bearer',
      });

      if (this.keycloakService.isLoggedIn()) {
        this.registerSessionExpiryGuards();
      }
    } catch (error) {
      console.error('Keycloak initialization failed', error);
      throw error;
    }
  }

  private registerSessionExpiryGuards(): void {
    if (this.hasRegisteredExpiryGuards) {
      return;
    }

    const keycloak = this.keycloakService.getKeycloakInstance();
    keycloak.onTokenExpired = () => {
      void this.forceLogout();
    };

    for (const eventName of this.activityEvents) {
      document.addEventListener(eventName, this.handleUserActivity, true);
    }

    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
      true
    );
    globalThis.addEventListener('focus', this.handleWindowFocus, true);
    this.hasRegisteredExpiryGuards = true;
  }

  private isSessionExpired(): boolean {
    const keycloak = this.keycloakService.getKeycloakInstance();

    if (!keycloak.authenticated || !keycloak.token) {
      return false;
    }

    try {
      return keycloak.isTokenExpired(0);
    } catch {
      return false;
    }
  }

  private async forceLogout(): Promise<void> {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    try {
      await this.keycloakService.logout(globalThis.location.origin);
    } catch (error) {
      console.error('Forced logout failed', error);
      globalThis.location.assign(globalThis.location.origin);
    }
  }
}
